import { Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EmptyState } from '../EmptyState';
import { useToast } from '../MessageBox';
import { useTranslation } from '../../hooks/useTranslation';
import { ATLAS_COLUMNS, FACE_ORDER, loadBlockFaces, overColour, packAtlas, solidFace } from '../../mapart/blockFaces';
import type { PreviewMesh } from '../../mapart/previewMesh';
import type { Rgb } from '../../mapart/types';
import styles from './SchematicPreview.module.css';

// Minecraft's fixed face shading: top, bottom, north, south, west, east.
const VERTEX_SHADER = `
precision highp float;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
in vec3 position;
in vec4 face;
out vec2 textureUv;
flat out vec2 tile;
flat out float shade;

const vec3 ORIGIN[6] = vec3[6](vec3(0, 1, 0), vec3(0), vec3(0), vec3(0, 0, 1), vec3(0), vec3(1, 0, 0));
const vec3 AXIS_U[6] = vec3[6](vec3(1, 0, 0), vec3(1, 0, 0), vec3(1, 0, 0), vec3(1, 0, 0), vec3(0, 0, 1), vec3(0, 0, 1));
const vec3 AXIS_V[6] = vec3[6](vec3(0, 0, 1), vec3(0, 0, 1), vec3(0, 1, 0), vec3(0, 1, 0), vec3(0, 1, 0), vec3(0, 1, 0));
// Texture rows run downwards and each side is read from outside, so north and east mirror their u.
const vec2 FLIP[6] = vec2[6](vec2(0, 0), vec2(0, 1), vec2(1, 1), vec2(0, 1), vec2(0, 1), vec2(1, 1));
const float SHADE[6] = float[6](1.0, 0.5, 0.8, 0.8, 0.6, 0.6);

void main() {
  int packed = int(face.w + 0.5);
  int side = packed % 8;
  int tileIndex = (packed / 8) * 6 + side;
  vec3 local = ORIGIN[side] + AXIS_U[side] * position.x + AXIS_V[side] * position.y;
  textureUv = mix(position.xy, 1.0 - position.xy, FLIP[side]);
  tile = vec2(tileIndex % ${ATLAS_COLUMNS}, tileIndex / ${ATLAS_COLUMNS});
  shade = SHADE[side];
  gl_Position = projectionMatrix * modelViewMatrix * vec4(face.xyz + local, 1.0);
}
`;

// Nearest sampling inside a clamped tile never reaches a neighbour, at any mip level of the atlas.
const FRAGMENT_SHADER = `
precision highp float;
uniform sampler2D atlas;
uniform float atlasRows;
in vec2 textureUv;
flat in vec2 tile;
flat in float shade;
out vec4 fragColour;

void main() {
  vec2 inTile = clamp(textureUv, 0.0, 0.999);
  vec3 colour = texture(atlas, (tile + inTile) / vec2(${ATLAS_COLUMNS}, atlasRows)).rgb;
  fragColour = vec4(colour * shade, 1.0);
}
`;

const FIELD_OF_VIEW = 45;
/** Looking down from the south, so north stays at the top the way the flat views show it. */
const START_ELEVATION = (55 * Math.PI) / 180;

const colourOf = (mesh: PreviewMesh, materialIndex: number): Rgb => [
    mesh.colours[materialIndex * 4],
    mesh.colours[materialIndex * 4 + 1],
    mesh.colours[materialIndex * 4 + 2],
];

function uploadAtlas(material: THREE.RawShaderMaterial, tiles: Uint8Array[]) {
    const atlas = packAtlas(tiles);
    const [base] = atlas.levels;
    const texture = new THREE.DataTexture(base.data, base.width, base.height);
    texture.mipmaps = atlas.levels;
    texture.generateMipmaps = false;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestMipmapLinearFilter;
    texture.needsUpdate = true;
    material.uniforms.atlas.value?.dispose();
    material.uniforms.atlas.value = texture;
    material.uniforms.atlasRows.value = atlas.rows;
}

interface Props {
    mesh: PreviewMesh | null;
    /** Build still running: the placeholder spins instead of reporting an empty artwork. */
    busy: boolean;
}

// Raw three.js on purpose: MinecraftRenderer drives a first person camera over live chunks from the
// WASM mesher, and SkinViewer3D is fixed to a player model; neither takes a prebuilt block list.
export default function SchematicPreview(props: Props) {
    const { t } = useTranslation();
    const toast = useToast();
    const [unavailable, setUnavailable] = createSignal(false);
    let host: HTMLDivElement | undefined;
    let canvas: HTMLCanvasElement | undefined;
    let renderer: THREE.WebGLRenderer | null = null;
    let controls: OrbitControls | null = null;
    let object: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial> | null = null;
    let framedSize = '';
    let shownMesh: PreviewMesh | null = null;
    let reportedTextureFailure = false;
    let frame = 0;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(FIELD_OF_VIEW, 1, 0.1, 1000);

    const render = () => {
        if (frame || !renderer) return;
        frame = requestAnimationFrame(() => {
            frame = 0;
            renderer?.render(scene, camera);
        });
    };

    const frameBuild = (mesh: PreviewMesh) => {
        if (!controls) return;
        const radius = Math.hypot(mesh.width, mesh.height, mesh.length) / 2;
        const distance = (radius / Math.sin(((FIELD_OF_VIEW / 2) * Math.PI) / 180)) * 1.05;
        controls.target.set(mesh.width / 2, mesh.height / 2, mesh.length / 2);
        camera.position.set(
            mesh.width / 2,
            mesh.height / 2 + distance * Math.sin(START_ELEVATION),
            mesh.length / 2 + distance * Math.cos(START_ELEVATION),
        );
        camera.near = Math.max(0.05, radius / 2000);
        camera.far = radius * 40;
        camera.updateProjectionMatrix();
        controls.maxDistance = distance * 6;
        controls.update();
    };

    const disposeObject = () => {
        if (!object) return;
        scene.remove(object);
        object.geometry.dispose();
        object.material.uniforms.atlas.value?.dispose();
        object.material.dispose();
        object = null;
    };

    const show = (mesh: PreviewMesh) => {
        disposeObject();
        const geometry = new THREE.InstancedBufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 1, 0], 3));
        geometry.setIndex([0, 1, 2, 2, 1, 3]);
        geometry.setAttribute('face', new THREE.InstancedBufferAttribute(mesh.faces, 4));
        geometry.instanceCount = mesh.faceCount;

        const material = new THREE.RawShaderMaterial({
            glslVersion: THREE.GLSL3,
            vertexShader: VERTEX_SHADER,
            fragmentShader: FRAGMENT_SHADER,
            uniforms: { atlas: { value: null }, atlasRows: { value: 1 } },
            // Hidden faces never reach the mesh, so there is nothing left for culling to save.
            side: THREE.DoubleSide,
        });
        uploadAtlas(material, mesh.models.flatMap((_, materialIndex) => FACE_ORDER.map(() => solidFace(colourOf(mesh, materialIndex)))));
        object = new THREE.Mesh(geometry, material);
        // The geometry is one quad; the instances decide where it lands, so its bounds say nothing.
        object.frustumCulled = false;
        scene.add(object);
        shownMesh = mesh;
        void applyTextures(mesh);

        // A rebuild while orbiting keeps the camera; only a different footprint reframes it.
        const size = `${mesh.width}x${mesh.height}x${mesh.length}`;
        if (size !== framedSize) {
            framedSize = size;
            frameBuild(mesh);
        }
        render();
    };

    const applyTextures = async (mesh: PreviewMesh) => {
        const facesByMaterial = await Promise.all(
            mesh.models.map((model) => (model ? loadBlockFaces(model).catch(() => null) : Promise.resolve(null))),
        );
        if (shownMesh !== mesh || !object) return;
        const failed = mesh.models.some((model, materialIndex) => model && !facesByMaterial[materialIndex]);
        if (failed && !reportedTextureFailure) {
            reportedTextureFailure = true;
            toast?.warning(t('MAPART_PREVIEW_TEXTURES_FAILED'));
        }
        const tiles = facesByMaterial.flatMap((faces, materialIndex) => {
            const colour = colourOf(mesh, materialIndex);
            return FACE_ORDER.map((_, faceIndex) => (faces ? overColour(faces[faceIndex], colour) : solidFace(colour)));
        });
        uploadAtlas(object.material, tiles);
        render();
    };

    const resize = () => {
        if (!renderer || !host) return;
        const width = host.clientWidth;
        const height = host.clientHeight;
        if (width === 0 || height === 0) return;
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        render();
    };

    const resetCamera = () => {
        if (!props.mesh) return;
        frameBuild(props.mesh);
        render();
    };

    onMount(() => {
        if (!canvas || !host) return;
        try {
            renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        } catch {
            setUnavailable(true);
            return;
        }
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setClearColor(0x000000, 0);
        controls = new OrbitControls(camera, canvas);
        controls.screenSpacePanning = true;
        controls.minDistance = 2;
        controls.addEventListener('change', render);

        const observer = new ResizeObserver(resize);
        observer.observe(host);
        resize();

        onCleanup(() => {
            observer.disconnect();
            if (frame) cancelAnimationFrame(frame);
            disposeObject();
            controls?.dispose();
            renderer?.dispose();
            renderer = null;
        });
    });

    createEffect(() => {
        const mesh = props.mesh;
        if (!renderer) return;
        if (!mesh || mesh.faceCount === 0) {
            disposeObject();
            shownMesh = null;
            render();
            return;
        }
        show(mesh);
    });

    return (
        <div ref={host} class={styles.host}>
            <canvas ref={canvas} class={styles.canvas} onDblClick={resetCamera} />
            <Show when={unavailable()}>
                <div class={styles.overlay}>
                    <EmptyState icon="fa-cube" title={t('MAPART_PREVIEW_UNAVAILABLE')} hint={t('MAPART_PREVIEW_UNAVAILABLE_HINT')} />
                </div>
            </Show>
            <Show when={!unavailable() && (!props.mesh || props.mesh.faceCount === 0)}>
                <div class={styles.overlay}>
                    <Show when={!props.busy} fallback={<i class="fa-solid fa-spinner fa-spin fa-2x" />}>
                        <EmptyState icon="fa-cubes" title={t('MAPART_PREVIEW_EMPTY')} hint={t('MAPART_PREVIEW_EMPTY_HINT')} />
                    </Show>
                </div>
            </Show>
        </div>
    );
}
